// stores/auth.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/api/axios'

export const useAuthStore = defineStore('auth', () => {
  // État
  const user = ref(null)
  const token = ref(localStorage.getItem('access_token'))
  const refreshToken = ref(localStorage.getItem('refresh_token'))
  const isLoading = ref(false)
  const error = ref(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const userRole = computed(() => user.value?.role || null)
  const userName = computed(() => user.value?.username || user.value?.email || '')

  // Actions
  const clearError = () => {
    this.error = null
  }
  const login = async (credentials) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.post('/auth-token', {
        username: credentials.email,
        password: credentials.password,
      })

      const { token, user: userData } = response.data
      // user.value = userData

      localStorage.setItem('access_token', token)
      // localStorage.setItem('user', JSON.stringify(userData))

      return { success: true, data: response.data }
    } catch (err) {
      error.value =
        err.response?.data?.message ||
        err.response?.data?.non_field_errors[0] ||
        'Erreur de connexion'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const register = async (userData) => {
    isLoading.value = true
    error.value = null

    try {
      let data =  {
        username: userData.username,
        email: userData.email,
        password: userData.password,
        first_name: userData.first_name,
        last_name: userData.last_name,
        phone: userData.phone
      }

      const response = await apiClient.post('/user/create/',data)


      return { success: true, data: response.data }
    } catch (err) {
      error.value = err.response?.data?.message || err.response?.data || "Erreur lors de l'inscription"
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      if (refreshToken.value) {
        await apiClient.post('/logout/', {
          refresh: refreshToken.value,
        })
      }
    } catch (err) {
      console.warn('Erreur lors du logout côté serveur:', err)
    } finally {
      user.value = null
      token.value = null
      refreshToken.value = null
      error.value = null

      localStorage.removeItem('access_token')
      // localStorage.removeItem('refresh_token')
      // localStorage.removeItem('user')
    }
  }

  const refreshAccessToken = async () => {
    try {
      const response = await apiClient.post('auth/token/refresh/', {
        refresh: refreshToken.value,
      })

      const { access } = response.data
      token.value = access
      localStorage.setItem('access_token', access)

      return access
    } catch (err) {
      await logout()
      throw err
    }
  }

  const fetchUser = async () => {
    try {
      const response = await apiClient.get('/user/')
      user.value = response.data
      localStorage.setItem('user', JSON.stringify(response.data))
      return user.value
    } catch (err) {
      console.error("Erreur lors de la récupération de l'utilisateur:", err)
      if (err.response?.status === 401) {
        await logout()
      }
    }
  }

  const updateProfile = async (profileData) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.patch('/user/', profileData)
      user.value = { ...user.value, ...response.data }
      localStorage.setItem('user', JSON.stringify(user.value))

      return { success: true, data: response.data }
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors de la mise à jour'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const changePassword = async (passwordData) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.post('auth/change-password/', {
        old_password: passwordData.oldPassword,
        new_password: passwordData.newPassword,
      })

      return { success: true, data: response.data }
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors du changement de mot de passe'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const resetPassword = async (email) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.post('password-reset/', { email })
      return { success: true, data: response.data }
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors de la demande de réinitialisation'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const confirmPasswordReset = async (token, password) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.post('password-reset-confirm/'+token+"/", {
        new_password1:password,
        new_password2:password,
      })
      return { success: true, data: response.data }
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors de la réinitialisation'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const initializeAuth = async () => {
    const storedUser = localStorage.getItem('user')
    if (storedUser && token.value) {
      try {
        user.value = JSON.parse(storedUser)
        await fetchUser()
      } catch (err) {
        await logout()
      }
    }
  }

  return {
    // State
    user,
    token,
    refreshToken,
    isLoading,
    error,

    // Getters
    isAuthenticated,
    userRole,
    userName,

    // Actions
    login,
    register,
    logout,
    refreshAccessToken,
    fetchUser,
    updateProfile,
    changePassword,
    resetPassword,
    confirmPasswordReset,
    initializeAuth,
  }
})
